import { useContext, useEffect, useRef, useCallback, useState, Fragment } from 'react';

import { PerformanceAnalyticsContext } from './PerformanceAnalyticsProvider';
import { getPerformanceMark, type AppPerformanceProfilingEvent } from './';
import { fg } from '@atlaskit/platform-feature-flags/fg';
import { usePerformanceAnalytics } from './usePerformanceAnalytics';
import { componentWithCondition } from '@atlaskit/platform-feature-flags-react/component-with-condition';
import type { PropsWithChildren } from 'react';
import UFOLoadHold from '@atlaskit/react-ufo/load-hold';
import React from 'react';

// Maximum time we would expect an app initialization to take.
const HOLD_MS_APP_INIT_TIMEOUT = 6000;
const HOLD_MS_FRAME_INITIALISED = 2000;
const HOLD_MS_RESOURCE_REQUEST_TIMEOUT = 5000;
const HOLD_MS_CATCH_LATENT_REQUESTS = 350;

const holdResourceStartEvents: Set<AppPerformanceProfilingEvent['type']> = new Set([
	'product-api-request-initiated',
	'resolver-invoke-initiated',
]);
const holdResourceEndEvents: Set<AppPerformanceProfilingEvent['type']> = new Set([
	'product-api-requested',
	'resolver-invoked',
]);
const holdAffectingEvents = new Set<AppPerformanceProfilingEvent['type']>([
	...holdResourceStartEvents,
	...holdResourceEndEvents,
	'navigation-timing',
	'user-interacted',
]);

/**
 * Using events captured by the neaest PerformanceAnalyticsContext ancestor, manually
 * creates react-ufo holds to indicate pending Forge app TTAI.
 *
 * Holds are attributed to the closest UFOSegment or UFOThirdPartySegment ancestor.
 */
export const PerformanceHoldAttributionAnchor = ({
	children,
}: PropsWithChildren): React.JSX.Element => {
	const timelineRef = useContext(PerformanceAnalyticsContext);
	const { captureEvent } = usePerformanceAnalytics();

	const appRuntimeInitiated = useRef(false);
	const [holdState, setHoldState] = useState<'holding' | 'released'>('holding');
	const releaseTimeoutRef = useRef<NodeJS.Timeout | undefined>();
	const holdResourcesStartedCountRef = useRef(0);
	const holdResourcesFinishedCountRef = useRef(0);

	const releaseHold = useCallback(() => {
		clearTimeout(releaseTimeoutRef.current);
		setHoldState('released');
	}, [setHoldState]);

	const retainHoldFor = useCallback(
		(holdFor: number) => {
			clearTimeout(releaseTimeoutRef.current);
			releaseTimeoutRef.current = setTimeout(releaseHold, holdFor);
		},
		[releaseHold],
	);

	const handleEvent = useCallback(
		(event: AppPerformanceProfilingEvent) => {
			if (!holdAffectingEvents.has(event.type)) {
				return;
			}

			// Drop holds on user interaction (timeline invalidated).
			if (event.type === 'user-interacted') {
				releaseHold();
				return;
			}

			// Keep track of requests starting and finished, don't consider in holding
			// logic until the app has initialised.
			const resourceHoldStarted = holdResourceStartEvents.has(event.type);
			const resourceHoldFinished = holdResourceEndEvents.has(event.type);
			if (resourceHoldStarted) {
				holdResourcesStartedCountRef.current += 1;
			} else if (resourceHoldFinished) {
				holdResourcesFinishedCountRef.current += 1;
			}

			const activeResourceRequests =
				holdResourcesStartedCountRef.current - holdResourcesFinishedCountRef.current;

			// Retain unconditional hold until 'navigation-timing' is received to indicate
			// that the frame runtime has initialised correctly.
			if (!appRuntimeInitiated.current) {
				if (event.type === 'navigation-timing') {
					appRuntimeInitiated.current = true;
					retainHoldFor(
						activeResourceRequests > 0
							? HOLD_MS_RESOURCE_REQUEST_TIMEOUT
							: HOLD_MS_FRAME_INITIALISED,
					);
				}
				return;
			}

			// On new resource holds, reset to default resource request timeout.
			if (resourceHoldStarted) {
				retainHoldFor(HOLD_MS_RESOURCE_REQUEST_TIMEOUT);
			}

			// Retain hold until all active requests are finished.
			if (activeResourceRequests > 0) {
				return;
			}

			// No active resource requests, wait a moment to catch any new resource
			// requests initiated by the app runtime.
			retainHoldFor(HOLD_MS_CATCH_LATENT_REQUESTS);
		},
		[releaseHold, retainHoldFor],
	);

	// Event listener management.
	useEffect(() => {
		// Default timeout for app init, so we don't indefinitely on e.g. for
		// unhandled app errors.
		retainHoldFor(HOLD_MS_APP_INIT_TIMEOUT);

		const { listeners } = timelineRef.current!;
		listeners.add(handleEvent);

		return () => {
			clearTimeout(releaseTimeoutRef.current);
			listeners.delete(handleEvent);
		};
	}, [timelineRef, handleEvent, retainHoldFor]);

	useEffect(() => {
		// On hold state change or component dismount: flag release in timeline.
		return () => {
			if (holdState === 'holding') {
				captureEvent({
					type: 'page-hold-released',
					elapsed: getPerformanceMark(),
				});
			}
		};
	}, [holdState, captureEvent]);

	return (
		<>
			<UFOLoadHold name="forge-ui-app-content-ttai-wait-v2" hold={holdState === 'holding'} />
			{children}
		</>
	);
};

export const PerformanceHoldAttributionAnchorRollout: React.FC<
	{
		children?: React.ReactNode | undefined;
	} & {
		children?: React.ReactNode | undefined;
	}
> = componentWithCondition(
	() => fg('forge-ui-app-perf-tracking-enabled'),
	PerformanceHoldAttributionAnchor,
	Fragment,
);
